import React, { memo, useCallback } from 'react';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import MenuFallback from '../../common/ui/menu-fallback/index.tsx';
import type MenuContent from './menu-content/index.tsx';
import MoreMenuCore from './more-menu-core/index.tsx';

type Props = {
	hideDoneItems: boolean;
	hasExpandedAllItems?: boolean;
	hasCollapsedAllItems?: boolean;
	hasFeedbackButton?: boolean;
	projectType?: ProjectType;
	isExpandCollapseButtonEnabled?: boolean;
	onChangeHideDoneItems: (hideDoneItems: boolean) => void;
	onExpandCollapseAllItems?: (operation: 'collapse' | 'expand') => void;
	hideItemsMenuOptions?: boolean;
};

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazyMenuContent = lazy<typeof MenuContent>(
	() => import(/* webpackChunkName: "async-jwm-common-more-menu-content" */ './menu-content'),
	{ ssr: false },
);

const MoreMenu = ({
	hideDoneItems,
	hasExpandedAllItems,
	hasCollapsedAllItems,
	hasFeedbackButton,
	projectType,
	isExpandCollapseButtonEnabled,
	onChangeHideDoneItems,
	onExpandCollapseAllItems,
	hideItemsMenuOptions,
}: Props) => {
	const renderContent = useCallback(
		(onCloseMenu: () => void) => (
			<Placeholder name="menu-content" fallback={<MenuFallback />}>
				<LazyMenuContent
					hideDoneItems={hideDoneItems}
					onChangeHideDoneItems={onChangeHideDoneItems}
					onExpandCollapseAllItems={onExpandCollapseAllItems}
					isExpandCollapseButtonEnabled={isExpandCollapseButtonEnabled}
					hasExpandedAllItems={hasExpandedAllItems}
					hasCollapsedAllItems={hasCollapsedAllItems}
					hasFeedbackButton={hasFeedbackButton}
					projectType={projectType}
					onCloseMenu={onCloseMenu}
					hideItemsMenuOptions={hideItemsMenuOptions}
				/>
			</Placeholder>
		),
		[
			hideDoneItems,
			onChangeHideDoneItems,
			onExpandCollapseAllItems,
			isExpandCollapseButtonEnabled,
			hasExpandedAllItems,
			hasCollapsedAllItems,
			hasFeedbackButton,
			projectType,
			hideItemsMenuOptions,
		],
	);

	return <MoreMenuCore renderContent={renderContent} />;
};

export default memo<Props>(MoreMenu);
